import {
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@new/lib';

export interface MultiSelectOption<T extends string = string> {
  label: string;
  value: T;
}

export interface MultiSelectProps<T extends string = string> {
  value: T[];
  onChange: (value: T[]) => void;
  options: MultiSelectOption<T>[];
  label?: string;
  labelClassName?: string;
  'aria-label'?: string;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  className?: string;
  onBlur?: () => void;
}

export const MultiSelect = <T extends string = string>({
  value,
  onChange,
  options,
  label,
  labelClassName,
  'aria-label': ariaLabel,
  placeholder = 'Select…',
  hasError,
  disabled,
  className,
}: MultiSelectProps<T>) => {
  const selected = options.filter((o) => value.includes(o.value));
  const displayValue = selected.length > 0 ? selected.map((o) => o.label).join(', ') : placeholder;

  return (
    <Field>
      {label ? (
        <Label
          className={cn(
            '[font-size:var(--fs-p4)]! mb-2 block font-medium text-(--neutral-black-NB1000)!',
            labelClassName
          )}
        >
          {label}
        </Label>
      ) : null}
      <Listbox value={value} onChange={onChange} multiple disabled={disabled}>
        <div className={cn('relative w-full', className)}>
          <ListboxButton
            data-slot="select"
            aria-label={label ? undefined : ariaLabel}
            className={cn(
              'l3! bg-(--neutral-white-NW800)! regular flex w-full items-center justify-between gap-2 border-solid',
              'h-[33px] md:h-[50px] xl:h-[66px] p-4! rounded-md! border-2! cursor-pointer',
              'disabled:cursor-not-allowed disabled:opacity-50',
              '[&&]:[font-family:var(--font-montserrat)]! [&&]:[font-size:var(--fs-l3)]! [&&]:leading(--lh-b)!',
              'focus:outline-none! focus:border-(--neutral-black-NB1000)/80!',
              hasError ? 'border-(--danger-D700)/80!' : 'border-transparent/80!'
            )}
          >
            <span
              className={cn(
                'truncate text-left',
                selected.length > 0
                  ? 'text-(--neutral-black-NB1000)!'
                  : 'text-(--neutral-white-NW600)!'
              )}
            >
              {displayValue}
            </span>
            <ChevronDown className="size-5 shrink-0 text-(--neutral-black-NB600)" />
          </ListboxButton>

          <ListboxOptions
            transition
            anchor="bottom start"
            className="z-[9999] mt-1 max-h-64 w-(--button-width) overflow-auto rounded-md border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) py-1 shadow-lg outline-none transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
          >
            {options.map((opt) => (
              <ListboxOption
                key={opt.value}
                value={opt.value}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left',
                  '[font-family:var(--font-montserrat)] [font-size:var(--fs-l4)] cursor-pointer',
                  'data-[focus]:bg-(--primary-O200)/40',
                  value.includes(opt.value)
                    ? 'font-semibold text-(--primary-O900)'
                    : 'text-(--neutral-black-NB900)'
                )}
              >
                {opt.label}
                {value.includes(opt.value) ? (
                  <Check className="size-4 shrink-0 text-(--primary-O900)" />
                ) : null}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </Field>
  );
};
